import React, {useEffect, useState} from "react";
import {
    Table,
    Card,
    Upload,
    Button,
    Modal,
    Form,
    DatePicker,
    TimePicker,
    Select,
    message,
    Popconfirm,
    Space,
    Divider,
    Row,
    Col
} from "antd";
import type {ColumnType} from "antd/es/table";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import api from "../../api";
import {CalendarOutlined, PlusOutlined, UploadOutlined} from "@ant-design/icons";

const {RangePicker} = DatePicker;
const {RangePicker: TimeRangePicker} = TimePicker;
const {Option} = Select;
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const RESPONSIBILITIES = ["Casino Club", "Daia", "Online", "Special Event"];

interface WorkSchedule {
    id: number;
    user: number;
    work_date: string;
    start_time: string;
    end_time: string;
    responsibilities: string[];
}

const WorkSchedulesPage: React.FC = () => {
    const [schedules, setSchedules] = useState<any[]>([]);
    const [totalSchedules, setTotalSchedules] = useState<number>(0);
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [form] = Form.useForm();
    const [dateFilter, setDateFilter] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [editingSchedule, setEditingSchedule] = useState<any | null>(null);
    const [todayFilterActive, setTodayFilterActive] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    useEffect(() => {
        fetchSchedules(pagination.current, pagination.pageSize);
        fetchUsers();
        checkAdmin();
    }, [pagination.current, pagination.pageSize]);

    const fetchSchedules = async (page = 1, pageSize = 10) => {
        setLoading(true)
        try {
            const res = await api.get("/users/work-schedules/", {
                params: {page, page_size: pageSize}
            });

            const data = res.data.results || res.data;

            setSchedules(data);
            setTotalSchedules(res.data.count || 0); // use this for pagination controls
        } catch (err) {
            message.error("Failed to fetch schedules");
        } finally {
            setLoading(false)
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await api.get("/users/", {params: {page: 1, page_size: 100}});
            setUsers(res.data.results || res.data);
        } catch (err) {
            message.error("Failed to fetch users");
        }
    };

    const checkAdmin = async () => {
        const res = await api.get("/users/me/");
        setIsAdmin(res.data.is_staff || res.data.is_superuser);
    };

    const openEditModal = (record: any | null) => {
        setEditingSchedule(record);
        if (record) {
            form.setFieldsValue({
                user: record.user,
                date: dayjs(record.work_date),
                timeRange: [dayjs(record.start_time), dayjs(record.end_time)],
                responsibilities: record.responsibilities,
            });
        } else {
            form.resetFields();
        }
        setFormVisible(true);
    };

    const handleSubmit = async (values: any) => {
        const [startTime, endTime] = values.timeRange;
        const selectedDate = dayjs(values.date).format("YYYY-MM-DD");

        const start = dayjs(`${selectedDate} ${dayjs(startTime).format("HH:mm")}`);
        const end = dayjs(`${selectedDate} ${dayjs(endTime).format("HH:mm")}`);

        const payload = {
            user: values.user,
            work_date: selectedDate,
            start_time: start.toISOString(),
            end_time: end.toISOString(),
            responsibilities: values.responsibilities || [],
        };

        try {
            if (editingSchedule) {
                await api.patch(`/users/work-schedules/${editingSchedule.id}/`, payload);
                message.success("Work schedule updated");
            } else {
                await api.post("/users/work-schedules/", payload);
                message.success("Work schedule created");
            }

            form.resetFields();
            setFormVisible(false);
            setEditingSchedule(null); // <-- clear editing state
            fetchSchedules(pagination.current, pagination.pageSize);
        } catch (err) {
            console.error("Failed to submit work schedule:", err);
            message.error("Failed to submit work schedule");
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await api.delete(`/users/work-schedules/${id}/`);
            message.success("Deleted successfully");
            fetchSchedules(pagination.current, pagination.pageSize);
        } catch {
            message.error("Delete failed");
        }
    };

    const handleExcelUpload = async (options: any) => {
        const {file, onSuccess, onError} = options;
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await api.post("/users/work-schedules-upload-excel/", formData, {
                headers: {"Content-Type": "multipart/form-data"},
            });

            message.success(response?.data?.message || "Work schedules uploaded");
            fetchSchedules(pagination.current, pagination.pageSize);
            onSuccess?.("ok");
        } catch (error: any) {
            const msg = error?.response?.data?.error || "Upload failed.";
            const details = error?.response?.data?.details || [];

            message.error(msg);

            if (details.length > 0) {
                Modal.error({
                    title: "Upload Failed Details",
                    content: (
                        <div>
                            {details.map((detail: string, idx: number) => (
                                <p key={idx} style={{marginBottom: 4}}>{detail}</p>
                            ))}
                        </div>
                    ),
                    width: 600,
                    onOk: () => fetchSchedules(pagination.current, pagination.pageSize),
                });
            }

            onError?.(error);
        }
    };

    const uploadProps = {
        name: "file",
        accept: ".xlsx, .xls",
        maxCount: 1,
        customRequest: handleExcelUpload,
        showUploadList: false,
    };

    const filteredSchedules = schedules.filter((item) => {
        if (!dateFilter) return true;
        const date = dayjs(item.work_date);
        return (
            date.isSameOrAfter(dateFilter[0], 'day') &&
            date.isSameOrBefore(dateFilter[1], 'day')
        );
    });

    const columns = [
        {title: "User", dataIndex: "user_name"},
        {title: "Phone", dataIndex: "user_phone"},
        {
            title: "Date",
            dataIndex: "work_date",
            render: (d: string) => {
                return dayjs(d).format("YYYY-MM-DD");
            }
        },
        {
            title: "Working Hours",
            render: (_: any, record: any) =>
                `${dayjs(record.start_time).format("HH:mm")} - ${dayjs(record.end_time).format("HH:mm")}`,
        },
        {
            title: "Responsibilities",
            dataIndex: "responsibilities",
            render: (arr: string[]) => arr?.join(", "),
        },
        isAdmin && {
            title: "Actions",
            render: (_: any, record: any) => (
                <Space>
                    <Button size="small" onClick={() => openEditModal(record)}>Edit</Button>
                    <Popconfirm title="Are you sure?" onConfirm={() => handleDelete(record.id)}>
                        <Button danger size="small">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ].filter(Boolean) as ColumnType<any>[];

    return (
        <Card
            title={<h2 className="text-lg font-semibold">Work Schedules</h2>}
            className="rounded-2xl shadow-sm"
            extra={
                <div style={{display: "flex", gap: "8px"}}>
                    <Upload {...uploadProps}>
                        <Button
                            icon={<UploadOutlined/>}
                            style={{backgroundColor: "#52c41a", color: "white"}}
                            className="rounded-lg"
                        >
                            Upload Excel
                        </Button>
                    </Upload>

                    <Button
                        type="primary"
                        icon={<PlusOutlined/>}
                        onClick={() => openEditModal(null)}
                        className="rounded-lg"
                    >
                        Add Schedule
                    </Button>
                </div>
            }
        >
            <Space style={{marginBottom: 16}} wrap>
                <RangePicker
                    style={{minWidth: 250}}
                    onChange={(dates) => {
                        setDateFilter(dates as [dayjs.Dayjs, dayjs.Dayjs]);
                        setTodayFilterActive(false);
                    }}
                    allowClear
                />
                <Button
                    icon={<CalendarOutlined/>}
                    type={todayFilterActive ? "default" : "primary"}
                    onClick={() => {
                        if (!todayFilterActive) {
                            const today = dayjs();
                            setDateFilter([today, today]);
                        } else {
                            setDateFilter(null);
                        }
                        setTodayFilterActive(!todayFilterActive);
                    }}
                    className="rounded-md"
                >
                    {todayFilterActive ? "Clear Today Filter" : "Today"}
                </Button>
            </Space>

            <Table
                dataSource={filteredSchedules}
                rowKey="id"
                columns={columns}
                scroll={{x: true}}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: totalSchedules,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({current: page, pageSize});
                    },
                }}
                onRow={(record) => {
                    const isPast = dayjs(record.work_date).isBefore(dayjs(), "day");
                    return {
                        style: {
                            backgroundColor: isPast ? "#f5f5f5" : "#ffffff",
                            cursor: "pointer",
                            transition: "background-color 0.3s ease",
                        },
                    };
                }}
                className="rounded-xl overflow-hidden"
            />

            <Modal
                open={formVisible}
                title={
                    <span className="text-base font-semibold">
                        {editingSchedule ? "Edit Work Schedule" : "Add Work Schedule"}
                    </span>
                }
                onCancel={() => {
                    setFormVisible(false);
                    setEditingSchedule(null);
                }}
                onOk={() => form.submit()}
                okText={editingSchedule ? "Update" : "Create"}
                centered
                width={600}
                okButtonProps={{className: "rounded-md"}}
                cancelButtonProps={{className: "rounded-md"}}
            >
                <Divider className="my-2"/>

                <Form
                    layout="vertical"
                    form={form}
                    onFinish={handleSubmit}
                    scrollToFirstError
                >
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="user"
                                label="User"
                                rules={[{required: true, message: "Please select a user"}]}
                            >
                                <Select placeholder="Select a user">
                                    {users.map((u) => (
                                        <Option key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="date"
                                label="Date"
                                rules={[{required: true, message: "Please select a date"}]}
                            >
                                <DatePicker
                                    style={{width: "100%"}}
                                    format="YYYY-MM-DD"
                                    placeholder="Select date"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name="timeRange"
                                label="Working Hours"
                                rules={[{required: true, message: "Please set working hours"}]}
                            >
                                <TimeRangePicker
                                    format="HH:mm"
                                    style={{width: "100%"}}
                                    placeholder={["Start time", "End time"]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="responsibilities" label="Responsibilities">
                                <Select
                                    mode="multiple"
                                    placeholder="Select responsibilities"
                                    allowClear
                                >
                                    {RESPONSIBILITIES.map((resp) => (
                                        <Option key={resp} value={resp}>
                                            {resp}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </Card>
    );
};

export default WorkSchedulesPage;